import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { ChevronDown } from 'lucide-react'

import { STUDIO_NAV_ITEM, isNavItemActive } from '@/constants/user-menu'
import { visibleStudioModeNavItems } from '@/constants/studio-nav'
import { useTenantFeatures } from '@/hooks'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'

type StudioNavLinkProps = {
  variant?: 'header' | 'drawer'
  onNavigate?: () => void
}

export function StudioNavLink({ variant = 'header', onNavigate }: StudioNavLinkProps) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { features } = useTenantFeatures()
  const modes = visibleStudioModeNavItems(features)
  const active = isNavItemActive(pathname, STUDIO_NAV_ITEM)

  if (modes.length === 0) return null

  if (modes.length === 1) {
    const mode = modes[0]
    const modeActive = pathname === mode.to || pathname.startsWith(`${mode.to}/`)
    return (
      <NavLink
        to={mode.to}
        onClick={onNavigate}
        className={clsx(
          variant === 'drawer' ? 'portal-drawer-link' : 'portal-nav-link',
          modeActive && 'is-active',
        )}
      >
        <span className="portal-nav-link__text">{t(STUDIO_NAV_ITEM.i18nKey)}</span>
      </NavLink>
    )
  }

  if (variant === 'drawer') {
    return (
      <div className="portal-drawer-studio-group">
        <div className="portal-drawer-studio-label">{t(STUDIO_NAV_ITEM.i18nKey)}</div>
        {modes.map((mode) => {
          const Icon = mode.icon
          const modeActive = pathname === mode.to || pathname.startsWith(`${mode.to}/`)
          return (
            <NavLink
              key={mode.key}
              to={mode.to}
              onClick={onNavigate}
              className={clsx('portal-drawer-link portal-drawer-link--sub', modeActive && 'is-active')}
            >
              <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              <span className="portal-nav-link__text">{t(mode.titleKey)}</span>
            </NavLink>
          )
        })}
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={clsx('portal-nav-link portal-nav-link--menu', active && 'is-active')}
          aria-haspopup="menu"
        >
          <span className="portal-nav-link__text">{t(STUDIO_NAV_ITEM.i18nKey)}</span>
          <ChevronDown className="portal-nav-link__chevron" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="min-w-[12.5rem] border-[#2a2a3a] bg-[#14141d] p-1.5"
      >
        {modes.map((mode) => {
          const Icon = mode.icon
          return (
            <DropdownMenuItem key={mode.key} asChild className="p-0 focus:bg-transparent">
              <NavLink
                to={mode.to}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left outline-none hover:bg-indigo-500/10 focus-visible:bg-indigo-500/10"
              >
                <Icon className="h-4 w-4 shrink-0 text-indigo-300/80" aria-hidden />
                <span className="text-sm font-medium text-zinc-100">{t(mode.titleKey)}</span>
              </NavLink>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
